import { Tag } from '@/components/ui/tag';
import { MOTION } from '@/utils';
import { CLIENT_LOGOS } from '@/utils/data/clients';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { useRef } from 'react';

const MIN_DISPLAY_MS = 7_000;
const GLOBAL_INTERVAL_S = 2;
const INITIAL_DELAY_S = 2;

export default function Clients() {
  const visibleLogos = CLIENT_LOGOS.slice(0, 8);

  const refs = {
    section: useRef<HTMLElement>(null),
    tag: useRef(null),
    title: useRef(null),
    cell: useRef<HTMLDivElement[]>([]),
    image: useRef<(HTMLImageElement | null)[]>([]),
  };

  const schedulerTimer = useRef<gsap.core.Tween | null>(null);
  const lastSwappedAt = useRef<number[]>([]);
  const displayedLogoByCell = useRef<number[]>([]);
  const nonDisplayedLogoPool = useRef<number[]>([]);

  useGSAP(
    () => {
      if (!refs.section.current || !refs.title.current) return;

      const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });
      const cells = refs.cell.current.filter(Boolean);
      const visibleCount = visibleLogos.length;

      displayedLogoByCell.current = Array.from({ length: visibleCount }, (_, index) => index);
      nonDisplayedLogoPool.current = Array.from(
        { length: Math.max(CLIENT_LOGOS.length - visibleCount, 0) },
        (_, index) => visibleCount + index,
      );
      lastSwappedAt.current = Array.from(
        { length: visibleCount },
        () => Date.now() - MIN_DISPLAY_MS,
      );

      const executeSwap = (cellIndex: number) => {
        const img = refs.image.current[cellIndex];
        if (!img || !nonDisplayedLogoPool.current.length) return;

        const currentLogoIndex = displayedLogoByCell.current[cellIndex];
        if (currentLogoIndex === undefined) return;

        const poolPickIndex = Math.floor(Math.random() * nonDisplayedLogoPool.current.length);
        const [nextLogoIndex] = nonDisplayedLogoPool.current.splice(poolPickIndex, 1);
        const next = CLIENT_LOGOS[nextLogoIndex];

        displayedLogoByCell.current[cellIndex] = nextLogoIndex;
        lastSwappedAt.current[cellIndex] = Date.now();

        gsap.to(img, {
          opacity: 0,
          scale: 0.85,
          duration: 0.4,
          ease: 'power2.in',
          onComplete: () => {
            img.src = next.src;
            img.alt = next.alt;
            nonDisplayedLogoPool.current.push(currentLogoIndex);

            gsap.fromTo(
              img,
              { opacity: 0, scale: 0.85 },
              {
                opacity: 1,
                scale: 1,
                duration: 0.5,
                ease: 'power3.out',
              },
            );
          },
        });
      };

      const tick = () => {
        const now = Date.now();
        const eligible = cells
          .map((_, i) => i)
          .filter((i) => now - lastSwappedAt.current[i] >= MIN_DISPLAY_MS);

        if (eligible.length && nonDisplayedLogoPool.current.length) {
          const cellIndex = eligible[Math.floor(Math.random() * eligible.length)];
          executeSwap(cellIndex);
        }

        schedulerTimer.current = gsap.delayedCall(GLOBAL_INTERVAL_S, tick);
      };

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 8 });
      gsap.set(cells, { autoAlpha: 0, y: 12 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: refs.section.current,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      });

      tl.to(refs.tag.current, {
        autoAlpha: 1,
        y: 0,
        duration: MOTION.durationSm,
        ease: MOTION.ease,
      })
        .from(
          splitTitle.lines,
          {
            yPercent: MOTION.yPercent,
            stagger: MOTION.staggerLines,
            duration: MOTION.durationMd,
            ease: MOTION.ease,
          },
          '<+0.15',
        )
        .to(
          cells,
          {
            autoAlpha: 1,
            y: 0,
            stagger: 0.04,
            duration: MOTION.durationSm,
            ease: MOTION.ease,
          },
          '<+0.2',
        )
        .add(() => {
          schedulerTimer.current = gsap.delayedCall(INITIAL_DELAY_S, tick);
        });

      return () => {
        splitTitle.revert();
        schedulerTimer.current?.kill();
      };
    },
    { scope: refs.section },
  );

  return (
    <section ref={refs.section} className="max-width-white-section" id="clients">
      <div className="max-width-white-section-content flex flex-col items-center gap-12">
        <div className="flex w-full flex-col gap-3">
          <Tag ref={refs.tag}>Plus de 500 cabinets</Tag>
          <h2 ref={refs.title} className="w-fit">
            Ils nous font confiance
          </h2>
        </div>

        <div className="border-dark-blue/7 grid w-full grid-cols-2 border-t border-l md:grid-cols-4">
          {visibleLogos.map((logo, index) => (
            <div
              key={logo.src}
              ref={(el) => {
                if (el) refs.cell.current[index] = el;
              }}
              className="border-dark-blue/7 hover:bg-dark-blue/2 group flex items-center justify-center border-r border-b py-4 transition-colors duration-300"
            >
              <img
                ref={(el) => {
                  refs.image.current[index] = el;
                }}
                alt={logo.alt}
                className="h-12 w-auto max-w-[100px] object-contain grayscale transition-[filter] duration-500 group-hover:grayscale-0 md:h-20 md:max-w-[130px]"
                decoding="async"
                loading="lazy"
                src={logo.src}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
